import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { SERVER_URL } from "../config/config";
import { CarsList } from "../constants/cars";
import {
  getNextCrimeTime,
  makeCrime,
} from "../helper/contractFunctions/MafiaBasicCrime";
import {
  buyBullets,
  claimBulletFactoryProfit,
  getBulletMarketLaunchBlock,
  getBulletMarketUpdateInterval,
  getCityMarketInfo,
  getNextBulletBuyTime,
  reproduceBullets,
} from "../helper/contractFunctions/MafiaBulletFactory";
import {
  getNextNickTime,
  nickCar,
} from "../helper/contractFunctions/MafiaCarNick";
import {
  approveGameCash,
  claimBankProfit,
  getCityCashProfit,
  getLastTransferTime,
  transferGameCash,
} from "../helper/contractFunctions/MafiaGameBank";
import {
  endBoozeBot,
  endBulletBot,
  endCarBot,
  endCrimeBot,
  endKSBot,
  endNarcsBot,
  getUserBoozeBotInfo,
  getUserBulletBotInfo,
  getUserBulletBotPlusInfo,
  getUserCarBotInfo,
  getUserCrimeBotInfo,
  getUserKSBotInfo,
  getUserNarcsBotInfo,
  startBoozeBot,
  startBulletBot,
  startCarBot,
  startCrimeBot,
  startKSBot,
  startNarcsBot,
} from "../helper/contractFunctions/MafiaHelperBot";
import { approveHelperCredit } from "../helper/contractFunctions/MafiaHelperCredit";
import {
  mintNewCars,
  repairCar,
  sellCar,
  shipCar,
} from "../helper/contractFunctions/MafiaInventory";
import {
  bustOut,
  buyOut,
  getCrimeType,
  getJailedUntil,
  getNextBustOutTime,
} from "../helper/contractFunctions/MafiaJail";
import {
  getNextTrainTime,
  trainSkill,
} from "../helper/contractFunctions/MafiaKillSkill";
import {
  buyBooze,
  buyNarcs,
  claimBoozeProfit,
  claimNarcsProfit,
  getAmountLimit,
  getBoozeTotalProfit,
  getCityMarketPrice,
  getLaunchBlock,
  getNarcoTotalProfit,
  getNextBoozeTime,
  getNextNarcsTime,
  getUpdateInterval,
  sellBooze,
  sellNarcs,
} from "../helper/contractFunctions/MafiaSmuggleMarket";
import { getInventoryItemInfoById } from "../helper/profile";
import { BusinessProfile } from "../types/BusinessProfile";
import { CarInventoryType } from "../types/CarInventoryType";
import { HelperBotInfo } from "../types/Contract/HelperBot/HelperBotInfo";
import { SmugglingGood } from "../types/Smuggling/SmugglingGood";
import { SmugglingInfo } from "../types/SmugglingInfo";
import { BulletBotPlusInfo } from "../types/Contract/HelperBot/BulletBotPlusInfo";

const DefaultHelperBotInfo: HelperBotInfo = {
  successRate: 0,
  startTimestamp: 0,
  endTimestamp: 0,
  attemptCount: 0,
  isRunning: false,
};

const DefaultBulletBotPlusInfo: BulletBotPlusInfo = {
  amountModifiedPercent: 0,
  priceModifiedPercent: 0,
};

export interface GameState {
  isTrainingKill: boolean;
  nextTrainTime: number;

  isMakingCrime: boolean;
  nextCrimeTime: number;

  isNickingCar: boolean;
  nextNickTime: number;

  isApprovingGameCash: boolean;

  isSmuggling: boolean;

  boozeHoldings: SmugglingGood[];
  boozePrices: number[];
  nextBoozeTime: number;
  boozeCountLimit: number;
  isLoadingBoozeHoldings: boolean;
  isLoadingBoozePrices: boolean;

  narcsHoldings: SmugglingGood[];
  narcsPrices: number[];
  nextNarcsTime: number;
  narcsCountLimit: number;
  isLoadingNarcsPrices: boolean;
  isLoadingNarcsHoldings: boolean;

  jailedUntil: number;
  nextBustOutTime: number;
  myCrimeType: number;

  isBuyingOut: boolean;
  isBustingOut: boolean;

  businessProfile: BusinessProfile | null;
  loadingBusinessProfile: boolean;

  smugglingMarketLaunchBlock: number;
  smugglingMarketUpdateInterval: number;

  smugglingInfo: SmugglingInfo;
  isLoadingSmugglingInfo: boolean;
  isClaimingSmugglingProfit: boolean;
  boozeTotalProfit: number;
  narcoTotalProfit: number;

  isLoadingCarAction: boolean;

  isLoadingGetGarageList: boolean;
  myGarageList: CarInventoryType[];

  isLoadingHelperBotInfo: boolean;
  userCrimeBotInfo: HelperBotInfo;
  userCarBotInfo: HelperBotInfo;
  userKSBotInfo: HelperBotInfo;
  userBoozeBotInfo: HelperBotInfo;
  userNarcsBotInfo: HelperBotInfo;
  userBulletBotInfo: HelperBotInfo;
  userBulletBotPlusInfo: BulletBotPlusInfo;

  isApprovingHelperCredit: boolean;
  isStartingHelperBot: boolean;
  isEndingHelperBot: boolean;

  bulletMarketInfo: {
    price: number;
    amountLeft: number;
    lastUpdateTime: number;
  };
  isLoadingBulletMarketInfo: boolean;
  bulletMarketLaunchBlock: number;
  bulletMarketUpdateInterval: number;
  isBuyingBullet: boolean;
  nextBulletBuyTime: number;
  isProducingBullet: boolean;

  isTransferringGameCash: boolean;
  lastTransferTime: number;
}

const initialState: GameState = {
  isTrainingKill: false,
  nextTrainTime: -1,

  isMakingCrime: false,
  nextCrimeTime: -1,

  isNickingCar: false,
  nextNickTime: -1,

  isApprovingGameCash: false,

  isSmuggling: false,

  boozeHoldings: [],
  boozePrices: [],
  nextBoozeTime: -1,
  boozeCountLimit: 0,
  isLoadingBoozeHoldings: false,
  isLoadingBoozePrices: false,
  boozeTotalProfit: 0,
  narcoTotalProfit: 0,

  narcsHoldings: [],
  narcsPrices: [],
  nextNarcsTime: -1,
  narcsCountLimit: 0,
  isLoadingNarcsHoldings: false,
  isLoadingNarcsPrices: false,

  jailedUntil: -1,
  nextBustOutTime: -1,
  myCrimeType: 0,
  isBuyingOut: false,
  isBustingOut: false,

  businessProfile: null,
  loadingBusinessProfile: false,

  smugglingMarketLaunchBlock: -1,
  smugglingMarketUpdateInterval: 200,

  smugglingInfo: {
    totalBuyAmount: 0,
    totalSellAmount: 0,
    totalOwnerProfit: 0,
  },
  isLoadingSmugglingInfo: false,
  isClaimingSmugglingProfit: false,

  isLoadingCarAction: false,

  isLoadingGetGarageList: false,
  myGarageList: [],

  isLoadingHelperBotInfo: false,
  userCrimeBotInfo: DefaultHelperBotInfo,
  userCarBotInfo: DefaultHelperBotInfo,
  userKSBotInfo: DefaultHelperBotInfo,
  userBoozeBotInfo: DefaultHelperBotInfo,
  userNarcsBotInfo: DefaultHelperBotInfo,
  userBulletBotInfo: DefaultHelperBotInfo,
  userBulletBotPlusInfo: DefaultBulletBotPlusInfo,

  isApprovingHelperCredit: false,
  isStartingHelperBot: false,
  isEndingHelperBot: false,

  bulletMarketInfo: {
    price: 0,
    amountLeft: 0,
    lastUpdateTime: 0,
  },
  isLoadingBulletMarketInfo: false,
  bulletMarketLaunchBlock: 0,
  bulletMarketUpdateInterval: 200,
  isBuyingBullet: false,
  nextBulletBuyTime: 0,
  isProducingBullet: false,

  isTransferringGameCash: false,
  lastTransferTime: 0,
};

export const getBulletCityMarketInfoAction = createAsyncThunk(
  "game/getCityMarketInfoAction",
  async ({ cityId }: { cityId: number }) => {
    const marketInfo = await getCityMarketInfo(cityId);

    return marketInfo;
  }
);

export const reproduceBulletsAction = createAsyncThunk(
  "game/reproduceBulletsAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    const result = await reproduceBullets(account, cityId);

    return result.transactionHash;
  }
);

export const claimBulletFactoryProfitAction = createAsyncThunk(
  "game/claimBulletFactoryProfitAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    const result = await claimBulletFactoryProfit(account, cityId);

    return result.transactionHash;
  }
);

export const claimBoozeProfitAction = createAsyncThunk(
  "game/claimBoozeProfitAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    const result = await claimBoozeProfit(account, cityId);

    return result;
  }
);

export const claimNarcsProfitAction = createAsyncThunk(
  "game/claimNarcsProfitAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    const result = await claimNarcsProfit(account, cityId);

    return result;
  }
);

export const trainSkillAction = createAsyncThunk(
  "game/trainSkillAction",
  async ({ account, trainType }: { account: string; trainType: number }) => {
    const result = await trainSkill(account, trainType);

    return result.transactionHash;
  }
);

export const getNextTrainTimeAction = createAsyncThunk(
  "game/getNextTrainTimeAction",
  async (account: string) => {
    const result = await getNextTrainTime(account);

    return result;
  }
);

export const getNextBulletBuyTimeAction = createAsyncThunk(
  "game/getNextBulletBuyTimeAction",
  async (account: string) => {
    const result = await getNextBulletBuyTime(account);

    return result;
  }
);

export const nickCarAction = createAsyncThunk(
  "game/nickCarAction",
  async ({
    account,
    crimeType,
    signMsg,
    signature,
  }: {
    account: string;
    crimeType: number;
    signMsg: string;
    signature: string;
  }) => {
    const result = await nickCar(crimeType, account, signMsg, signature);

    return result.transactionHash;
  }
);

export const getNextNickCarAction = createAsyncThunk(
  "game/getNextNickCarAction",
  async (account: string) => {
    const result = await getNextNickTime(account);

    return result;
  }
);

export const getBoozeHoldingsAction = createAsyncThunk(
  "game/getBoozeHoldingsAction",
  async (account: string) => {
    const response = await axios.get(
      `${SERVER_URL}/profile/goods/holding/${account}?categoryId=0`
    );

    return response.data;
  }
);

export const getNarcsHoldingsAction = createAsyncThunk(
  "game/getNarcsHoldingsAction",
  async (account: string) => {
    const response = await axios.get(
      `${SERVER_URL}/profile/goods/holding/${account}?categoryId=1`
    );

    return response.data;
  }
);

export const makeCrimeAction = createAsyncThunk(
  "game/makeCrimeAction",
  async ({ crimeType, account }: { crimeType: number; account: string }) => {
    const result = await makeCrime(crimeType, account);

    return result.transactionHash;
  }
);

export const getNextCrimeTimeAction = createAsyncThunk(
  "game/getNextCrimeTimeAction",
  async (account: string) => {
    const result = await getNextCrimeTime(account);
    return result;
  }
);

export const getCityMarketPriceAction = createAsyncThunk(
  "game/getCityMarketPriceAction",
  async (cityId: number) => {
    const result = await getCityMarketPrice(cityId);
    return result;
  }
);

export const getNextBoozeTimeAction = createAsyncThunk(
  "game/getNextBoozeTimeAction",
  async (account: string) => {
    const result = await getNextBoozeTime(account);
    return result;
  }
);

export const getNextNarcsTimeAction = createAsyncThunk(
  "game/getNextNarcsTimeAction",
  async (account: string) => {
    const result = await getNextNarcsTime(account);
    return result;
  }
);

export const getAmountLimitAction = createAsyncThunk(
  "game/getAmountLimitAction",
  async (account: string) => {
    const result = await getAmountLimit(account);
    return result;
  }
);

export const getSmugglingMarketLaunchBlockAction = createAsyncThunk(
  "game/getSmugglingMarketLaunchBlockAction",
  async () => {
    const result = await getLaunchBlock();
    return result;
  }
);

export const getSmugglingMarketUpdateIntervalAction = createAsyncThunk(
  "game/getSmugglingMarketUpdateIntervalAction",
  async () => {
    const result = await getUpdateInterval();
    return result;
  }
);

export const getBulletMarketLaunchBlockAction = createAsyncThunk(
  "game/getBulletMarketLaunchBlockAction",
  async () => {
    const result = await getBulletMarketLaunchBlock();
    return result;
  }
);

export const getBulletMarketUpdateIntervalAction = createAsyncThunk(
  "game/getBulletMarketUpdateIntervalAction",
  async () => {
    const result = await getBulletMarketUpdateInterval();
    return result;
  }
);

export const approveGameCashAction = createAsyncThunk(
  "game/approveGameCashAction",
  async ({
    cashAmount,
    account,
    spender,
  }: {
    cashAmount: number;
    account: string;
    spender: string;
  }) => {
    const data = await approveGameCash(cashAmount, spender, account);

    return data.transactionHash || "";
  }
);

export const getGarageListAction = createAsyncThunk(
  "game/getGarageListAction",
  async ({ account }: { account: string }) => {
    const data = await axios.get(
      `${SERVER_URL}/profile/carNick/garage?address=${account}`
    );

    return data.data;
  }
);

export const buyBoozeAction = createAsyncThunk(
  "game/buyBoozeAction",
  async ({
    account,
    types,
    message,
    signature,
  }: {
    account: string;
    types: number[];
    message: string;
    signature: string;
  }) => {
    const data = await buyBooze(account, types, message, signature);

    return data;
  }
);

export const sellBoozeAction = createAsyncThunk(
  "game/sellBoozeAction",
  async ({
    account,
    itemIds,
    signMsg,
    signature,
  }: {
    account: string;
    itemIds: number[];
    signMsg: string;
    signature: string;
  }) => {
    const data = await sellBooze(account, itemIds, signMsg, signature);

    return data;
  }
);

export const buyNarcsAction = createAsyncThunk(
  "game/buyNarcsAction",
  async ({
    account,
    types,
    signMsg,
    signature,
  }: {
    account: string;
    types: number[];
    signMsg: string;
    signature: string;
  }) => {
    const data = await buyNarcs(account, types, signMsg, signature);

    return data;
  }
);

export const sellNarcsAction = createAsyncThunk(
  "game/sellNarcsAction",
  async ({
    account,
    itemIds,
    signMsg,
    signature,
  }: {
    account: string;
    itemIds: number[];
    signMsg: string;
    signature: string;
  }) => {
    const data = await sellNarcs(account, itemIds, signMsg, signature);

    return data;
  }
);

export const buyOutAction = createAsyncThunk(
  "game/buyOutAction",
  async ({ address, account }: { address: string; account: string }) => {
    const data = await buyOut(address, account);
    return data.transactionHash;
  }
);

export const bustOutAction = createAsyncThunk(
  "game/bustOutAction",
  async ({ address, account }: { address: string; account: string }) => {
    const data = await bustOut(address, account);
    return data.transactionHash;
  }
);

export const getJailedUntilAction = createAsyncThunk(
  "game/getJailedUntilAction",
  async (account: string) => {
    const result = await getJailedUntil(account);
    return result;
  }
);

export const getCrimeTypeAction = createAsyncThunk(
  "game/getCrimeTypeAction",
  async (account: string) => {
    const result = await getCrimeType(account);
    return result;
  }
);

export const buyBulletsAction = createAsyncThunk(
  "game/buyBulletsAction",
  async ({
    account,
    bulletAmount,
    message,
    signature,
  }: {
    account: string;
    bulletAmount: number;
    message: string;
    signature: string;
  }) => {
    const result = await buyBullets(account, bulletAmount, message, signature);

    return result.transactionHash;
  }
);

export const getBusinessProfileAction = createAsyncThunk(
  "game/getBusinessProfileAction",
  async ({ itemId, cityId }: { itemId: number; cityId: number }) => {
    const response = await axios.get(
      `${SERVER_URL}/business/detail?itemId=${itemId}&cityId=${cityId}`
    );

    if (itemId === 42) {
      const cityCashProfit = await getCityCashProfit(cityId);
      response.data.profit = cityCashProfit;
    }

    return response.data;
  }
);

export const getSmugglingInfoAction = createAsyncThunk(
  "game/getSmugglingInfoAction",
  async ({ categoryId, cityId }: { categoryId: number; cityId: number }) => {
    const response = await axios.get(
      `${SERVER_URL}/profile/goods/info?categoryId=${categoryId}&cityId=${cityId}`
    );
    return response.data;
  }
);

export const carRepairAction = createAsyncThunk(
  "game/carRepairAction",
  async ({ itemId, account }: { itemId: number; account: string }) => {
    const data = await repairCar(account, itemId);

    return data.transactionHash;
  }
);

export const carSellAction = createAsyncThunk(
  "game/carSellAction",
  async ({ itemId, account }: { itemId: number; account: string }) => {
    const data = await sellCar(account, itemId);

    return data.transactionHash;
  }
);

export const mintNewCarAction = createAsyncThunk(
  "game/mintNewCarAction",
  async ({
    account,
    itemIds,
    signMsg,
    signature,
  }: {
    account: string;
    itemIds: number[];
    signMsg: string;
    signature: string;
  }) => {
    const data = await mintNewCars(account, itemIds, signMsg, signature);
    return data.transactionHash;
  }
);

export const carShipAction = createAsyncThunk(
  "game/carShipAction",
  async ({
    itemId,
    account,
    cityId,
  }: {
    itemId: number;
    account: string;
    cityId: number;
  }) => {
    console.log(cityId);
    const data = await shipCar(account, itemId, cityId);

    return data.transactionHash;
  }
);

export const getUserHelperBotInfo = createAsyncThunk(
  "game/getUserHelperBotInfo",
  async (account: string) => {
    const userCrimeBotInfo = await getUserCrimeBotInfo(account);
    const userCarBotInfo = await getUserCarBotInfo(account);
    const userKSBotInfo = await getUserKSBotInfo(account);
    const userBoozeBotInfo = await getUserBoozeBotInfo(account);
    const userNarcsBotInfo = await getUserNarcsBotInfo(account);
    const userBulletBotInfo = await getUserBulletBotInfo(account);
    const userBulletBotPlusInfo = await getUserBulletBotPlusInfo(account);

    return {
      userCrimeBotInfo,
      userCarBotInfo,
      userKSBotInfo,
      userBoozeBotInfo,
      userNarcsBotInfo,
      userBulletBotInfo,
      userBulletBotPlusInfo,
    };
  }
);

export const startCrimeBotAction = createAsyncThunk(
  "game/startCrimeBotAction",
  async ({
    account,
    attemptCount,
  }: {
    account: string;
    attemptCount: number;
  }) => {
    const data = await startCrimeBot(account, attemptCount);

    return data.transactionHash;
  }
);

export const endCrimeBotAction = createAsyncThunk(
  "game/endCrimeBotAction",
  async (account: string) => {
    const data = await endCrimeBot(account);
    return data.transactionHash;
  }
);

export const startCarBotAction = createAsyncThunk(
  "game/startCarBotAction",
  async ({
    account,
    attemptCount,
  }: {
    account: string;
    attemptCount: number;
  }) => {
    const data = await startCarBot(account, attemptCount);
    return data.transactionHash;
  }
);

export const endCarBotAction = createAsyncThunk(
  "game/endCarBotAction",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const data = await endCarBot(account, signMsg, signature);
    return data.transactionHash;
  }
);

export const startKSBotAction = createAsyncThunk(
  "game/startKSBotAction",
  async ({
    account,
    attemptCount,
  }: {
    account: string;
    attemptCount: number;
  }) => {
    const data = await startKSBot(account, attemptCount);
    return data.transactionHash;
  }
);

export const endKSBotAction = createAsyncThunk(
  "game/endKSBotAction",
  async (account: string) => {
    const data = await endKSBot(account);
    return data.transactionHash;
  }
);

export const startBoozeBotAction = createAsyncThunk(
  "game/startBoozeBotAction",
  async ({
    account,
    attemptCount,
  }: {
    account: string;
    attemptCount: number;
  }) => {
    const data = await startBoozeBot(account, attemptCount);
    return data.transactionHash;
  }
);

export const endBoozeBotAction = createAsyncThunk(
  "game/endBoozeBotAction",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const data = await endBoozeBot(account, signMsg, signature);
    return data.transactionHash;
  }
);

export const startBulletDealerBotAction = createAsyncThunk(
  "game/startBulletDealerBotAction",
  async ({
    account,
    attemptCount,
  }: {
    account: string;
    attemptCount: number;
  }) => {
    const data = await startBulletBot(account, attemptCount);
    return data.transactionHash;
  }
);

export const endBulletDealerBotAction = createAsyncThunk(
  "game/endBulletDealerBotAction",
  async ({
    isAccepting,
    account,
    signMsg,
    signature,
  }: {
    isAccepting: boolean;
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const data = await endBulletBot(isAccepting, account, signMsg, signature);
    return data.transactionHash;
  }
);

export const getBoozeTotalProfitAction = createAsyncThunk(
  "game/getBoozeTotalProfitAction",
  async ({ city }: { city: number }) => {
    const data = await getBoozeTotalProfit(city);
    return data;
  }
);

export const getNarcoTotalProfitAction = createAsyncThunk(
  "game/getNarcoTotalProfitAction",
  async ({ city }: { city: number }) => {
    const data = await getNarcoTotalProfit(city);
    return data;
  }
);

export const startNarcsBotAction = createAsyncThunk(
  "game/startNarcsBotAction",
  async ({
    account,
    attemptCount,
  }: {
    account: string;
    attemptCount: number;
  }) => {
    const data = await startNarcsBot(account, attemptCount);
    return data.transactionHash;
  }
);

export const endNarcsBotAction = createAsyncThunk(
  "game/endNarcsBotAction",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const data = await endNarcsBot(account, signMsg, signature);
    return data.transactionHash;
  }
);

export const approveHelperCreditAction = createAsyncThunk(
  "game/approveHelperCreditAction",
  async ({
    account,
    spender,
    creditAmount,
  }: {
    account: string;
    spender: string;
    creditAmount: number;
  }) => {
    const data = await approveHelperCredit(creditAmount, spender, account);
    return data.transactionHash;
  }
);

export const getNextBustOutTimeAction = createAsyncThunk(
  "game/getNextBustOutTimeAction",
  async (account: string) => {
    const data = await getNextBustOutTime(account);
    return data;
  }
);

export const transferGameCashAction = createAsyncThunk(
  "game/transferGameCashAction",
  async ({
    account,
    amount,
    recipientAddress,
    signMsg,
    signature,
  }: {
    account: string;
    amount: number;
    recipientAddress: string;
    signMsg: string;
    signature: string;
  }) => {
    const data = await transferGameCash(
      account,
      amount,
      recipientAddress,
      signMsg,
      signature
    );
    return data;
  }
);

export const getLastTransferTimeAction = createAsyncThunk(
  "game/getLastTransferTimeAction",
  async (account: string) => {
    const data = await getLastTransferTime(account);
    return data;
  }
);

export const claimBankProfitAction = createAsyncThunk(
  "game/claimBankProfitAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    const data = await claimBankProfit(account, cityId);
    return data;
  }
);

export const gameSlice = createSlice({
  name: "game",
  initialState,
  reducers: {
    finishCrime: (state) => {
      state.isMakingCrime = false;
    },
    finishNick: (state) => {
      state.isNickingCar = false;
    },
    finishSmuggle: (state) => {
      state.isSmuggling = false;
    },
    finishTrain: (state) => {
      state.isTrainingKill = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(reproduceBulletsAction.pending, (state) => {
      state.isProducingBullet = true;
    });

    builder.addCase(reproduceBulletsAction.fulfilled, (state) => {
      state.isProducingBullet = false;
    });

    builder.addCase(reproduceBulletsAction.rejected, (state) => {
      state.isProducingBullet = false;
    });

    builder.addCase(buyBulletsAction.pending, (state) => {
      state.isBuyingBullet = true;
    });
    builder.addCase(buyBulletsAction.fulfilled, (state, { payload }) => {
      state.isBuyingBullet = false;
    });
    builder.addCase(buyBulletsAction.rejected, (state, { error }) => {
      state.isBuyingBullet = false;
    });

    builder.addCase(getBulletCityMarketInfoAction.pending, (state) => {
      state.isLoadingBulletMarketInfo = true;
    });
    builder.addCase(
      getBulletCityMarketInfoAction.fulfilled,
      (state, { payload }) => {
        state.bulletMarketInfo = payload;
        state.isLoadingBulletMarketInfo = false;
      }
    );
    builder.addCase(getBulletCityMarketInfoAction.rejected, (state) => {
      state.isLoadingBulletMarketInfo = false;
    });

    builder.addCase(trainSkillAction.pending, (state) => {
      state.isTrainingKill = true;
    });
    builder.addCase(trainSkillAction.fulfilled, (state, { payload }) => {});
    builder.addCase(trainSkillAction.rejected, (state) => {
      state.isTrainingKill = false;
    });

    builder.addCase(getNextTrainTimeAction.pending, () => {});
    builder.addCase(getNextTrainTimeAction.fulfilled, (state, { payload }) => {
      state.nextTrainTime = payload;
    });
    builder.addCase(getNextTrainTimeAction.rejected, (state) => {
      state.nextTrainTime = -1;
    });

    builder.addCase(getNextBulletBuyTimeAction.pending, () => {});
    builder.addCase(
      getNextBulletBuyTimeAction.fulfilled,
      (state, { payload }) => {
        state.nextBulletBuyTime = payload;
      }
    );
    builder.addCase(getNextBulletBuyTimeAction.rejected, (state) => {
      state.nextBulletBuyTime = -1;
    });

    builder.addCase(getNextNickCarAction.pending, () => {});
    builder.addCase(getNextNickCarAction.fulfilled, (state, { payload }) => {
      state.nextNickTime = payload;
    });
    builder.addCase(getNextNickCarAction.rejected, (state) => {
      state.nextCrimeTime = -1;
    });

    builder.addCase(getSmugglingMarketLaunchBlockAction.pending, () => {});
    builder.addCase(
      getSmugglingMarketLaunchBlockAction.fulfilled,
      (state, { payload }) => {
        state.smugglingMarketLaunchBlock = payload;
      }
    );
    builder.addCase(getSmugglingMarketLaunchBlockAction.rejected, () => {});

    builder.addCase(getSmugglingMarketUpdateIntervalAction.pending, () => {});
    builder.addCase(
      getSmugglingMarketUpdateIntervalAction.fulfilled,
      (state, { payload }) => {
        state.smugglingMarketUpdateInterval = payload;
      }
    );
    builder.addCase(getSmugglingMarketUpdateIntervalAction.rejected, () => {});

    builder.addCase(makeCrimeAction.pending, (state) => {
      state.isMakingCrime = true;
    });
    builder.addCase(makeCrimeAction.fulfilled, (state) => {});
    builder.addCase(makeCrimeAction.rejected, (state) => {
      state.isMakingCrime = false;
    });

    builder.addCase(getNextCrimeTimeAction.pending, () => {});
    builder.addCase(getNextCrimeTimeAction.fulfilled, (state, { payload }) => {
      state.nextCrimeTime = payload;
    });
    builder.addCase(getNextCrimeTimeAction.rejected, (state) => {
      state.nextCrimeTime = -1;
    });

    builder.addCase(getCityMarketPriceAction.pending, (state) => {
      state.isLoadingBoozePrices = true;
      state.isLoadingNarcsPrices = true;
    });
    builder.addCase(
      getCityMarketPriceAction.fulfilled,
      (state, { payload }) => {
        state.boozePrices = payload[0];
        state.narcsPrices = payload[1];
        state.isLoadingBoozePrices = false;
        state.isLoadingNarcsPrices = false;
      }
    );
    builder.addCase(getCityMarketPriceAction.rejected, (state, { error }) => {
      state.boozePrices = [];
      state.narcsPrices = [];

      state.isLoadingBoozePrices = false;
      state.isLoadingNarcsPrices = false;
    });

    builder.addCase(getNextBoozeTimeAction.pending, () => {});
    builder.addCase(getNextBoozeTimeAction.fulfilled, (state, { payload }) => {
      state.nextBoozeTime = payload;
    });
    builder.addCase(getNextBoozeTimeAction.rejected, (state) => {
      state.nextBoozeTime = -1;
    });

    builder.addCase(getNextNarcsTimeAction.pending, () => {});
    builder.addCase(getNextNarcsTimeAction.fulfilled, (state, { payload }) => {
      state.nextNarcsTime = payload;
    });
    builder.addCase(getNextNarcsTimeAction.rejected, (state) => {
      state.nextNarcsTime = -1;
    });

    builder.addCase(getAmountLimitAction.pending, () => {});
    builder.addCase(getAmountLimitAction.fulfilled, (state, { payload }) => {
      state.boozeCountLimit = payload[0];
      state.narcsCountLimit = payload[1];
    });
    builder.addCase(getAmountLimitAction.rejected, (state, { error }) => {
      state.boozeCountLimit = 0;
      state.narcsCountLimit = 0;
      console.log(error);
    });

    builder.addCase(approveGameCashAction.pending, (state) => {
      state.isApprovingGameCash = true;
    });

    builder.addCase(approveGameCashAction.fulfilled, (state) => {
      state.isApprovingGameCash = false;
    });

    builder.addCase(approveGameCashAction.rejected, (state) => {
      state.isApprovingGameCash = false;
    });

    builder.addCase(buyBoozeAction.pending, (state) => {
      state.isSmuggling = true;
    });
    builder.addCase(buyBoozeAction.fulfilled, (state) => {});
    builder.addCase(buyBoozeAction.rejected, (state, { error }) => {
      state.isSmuggling = false;
      console.log(error);
    });

    builder.addCase(sellBoozeAction.pending, (state) => {
      state.isSmuggling = true;
    });
    builder.addCase(sellBoozeAction.fulfilled, (state) => {});
    builder.addCase(sellBoozeAction.rejected, (state, { error }) => {
      state.isSmuggling = false;
      console.log(error);
    });

    builder.addCase(buyNarcsAction.pending, (state) => {
      state.isSmuggling = true;
    });
    builder.addCase(buyNarcsAction.fulfilled, (state) => {});
    builder.addCase(buyNarcsAction.rejected, (state, { error }) => {
      state.isSmuggling = false;

      console.log(error);
    });

    builder.addCase(sellNarcsAction.pending, (state) => {
      state.isSmuggling = true;
    });
    builder.addCase(sellNarcsAction.fulfilled, (state) => {});
    builder.addCase(sellNarcsAction.rejected, (state, { error }) => {
      state.isSmuggling = false;
      console.log(error);
    });

    builder.addCase(getBoozeHoldingsAction.pending, (state) => {
      state.isLoadingBoozeHoldings = true;
    });
    builder.addCase(getBoozeHoldingsAction.fulfilled, (state, { payload }) => {
      state.isLoadingBoozeHoldings = false;
      state.boozeHoldings = payload;
    });
    builder.addCase(getBoozeHoldingsAction.rejected, (state, { error }) => {
      state.isLoadingBoozeHoldings = false;
    });

    builder.addCase(getNarcsHoldingsAction.pending, (state) => {
      state.isLoadingBoozeHoldings = true;
    });
    builder.addCase(getNarcsHoldingsAction.fulfilled, (state, { payload }) => {
      state.isLoadingBoozeHoldings = false;
      state.narcsHoldings = payload;
    });
    builder.addCase(getNarcsHoldingsAction.rejected, (state, { error }) => {
      state.isLoadingBoozeHoldings = false;
    });

    builder.addCase(getJailedUntilAction.pending, () => {});
    builder.addCase(getJailedUntilAction.fulfilled, (state, { payload }) => {
      state.jailedUntil = payload;
    });
    builder.addCase(getJailedUntilAction.rejected, (state) => {
      state.jailedUntil = -1;
    });

    builder.addCase(getCrimeTypeAction.pending, () => {});
    builder.addCase(getCrimeTypeAction.fulfilled, (state, { payload }) => {
      state.myCrimeType = payload;
    });
    builder.addCase(getCrimeTypeAction.rejected, (state, { error }) => {});

    builder.addCase(buyOutAction.pending, (state) => {
      state.isBuyingOut = true;
    });
    builder.addCase(buyOutAction.fulfilled, (state) => {
      state.isBuyingOut = false;
    });
    builder.addCase(buyOutAction.rejected, (state, { error }) => {
      state.isBuyingOut = false;
      console.log(error);
    });

    builder.addCase(bustOutAction.pending, (state) => {
      state.isBustingOut = true;
    });
    builder.addCase(bustOutAction.fulfilled, (state) => {
      state.isBustingOut = false;
    });
    builder.addCase(bustOutAction.rejected, (state, { error }) => {
      state.isBustingOut = false;
      console.log(error);
    });

    builder.addCase(getBusinessProfileAction.pending, (state) => {
      state.loadingBusinessProfile = true;
    });
    builder.addCase(
      getBusinessProfileAction.fulfilled,
      (state, { payload }) => {
        state.loadingBusinessProfile = false;
        state.businessProfile = {
          id: payload.id,
          userId: payload.user_id,
          address: payload.address,
          itemId: payload.item_id,
          categoryId: getInventoryItemInfoById(payload.item_id)?.categoryId,
          typeId: getInventoryItemInfoById(payload.item_id)?.typeId,
          cityId: payload.city,
          removed: payload.removed,
          status: payload.status,
          timestamp: payload.timestamp,
          map: payload.Map,
          ownerName: payload.ownerName,
          ownerImageId: payload.ownerImageId,
          ownerGender: payload.ownerGender,
          familyInfo: payload.familyInfo,
          profit: payload.profit,
          feeAmount: payload.feeAmount,
        } as BusinessProfile;
      }
    );
    builder.addCase(getBusinessProfileAction.rejected, (state, { error }) => {
      state.loadingBusinessProfile = false;
      console.log(error);
    });

    builder.addCase(nickCarAction.pending, (state) => {
      state.isNickingCar = true;
    });
    builder.addCase(nickCarAction.fulfilled, (state) => {
      state.isNickingCar = false;
    });

    builder.addCase(nickCarAction.rejected, (state, { error }) => {
      state.isNickingCar = false;
      console.log(error);
    });

    builder.addCase(getSmugglingInfoAction.pending, (state) => {
      state.isLoadingSmugglingInfo = true;
    });
    builder.addCase(getSmugglingInfoAction.fulfilled, (state, { payload }) => {
      state.isLoadingSmugglingInfo = false;
      state.smugglingInfo = payload;
    });
    builder.addCase(getSmugglingInfoAction.rejected, (state, { error }) => {
      state.isLoadingSmugglingInfo = false;
      console.log(error);
    });

    builder.addCase(carSellAction.pending, (state) => {
      state.isLoadingCarAction = true;
    });
    builder.addCase(carSellAction.fulfilled, (state, { payload }) => {
      state.isLoadingCarAction = false;
    });
    builder.addCase(carSellAction.rejected, (state) => {
      state.isLoadingCarAction = false;
    });

    builder.addCase(carShipAction.pending, (state) => {
      state.isLoadingCarAction = true;
    });
    builder.addCase(carShipAction.fulfilled, (state, { payload }) => {
      state.isLoadingCarAction = false;
    });
    builder.addCase(carShipAction.rejected, (state) => {
      state.isLoadingCarAction = false;
    });

    builder.addCase(carRepairAction.pending, (state) => {
      state.isLoadingCarAction = true;
    });
    builder.addCase(carRepairAction.fulfilled, (state, { payload }) => {
      state.isLoadingCarAction = false;
    });
    builder.addCase(carRepairAction.rejected, (state) => {
      state.isLoadingCarAction = false;
    });

    builder.addCase(mintNewCarAction.pending, (state) => {
      state.isLoadingCarAction = true;
    });
    builder.addCase(mintNewCarAction.fulfilled, (state, { payload }) => {
      state.isLoadingCarAction = false;
    });
    builder.addCase(mintNewCarAction.rejected, (state) => {
      state.isLoadingCarAction = false;
    });

    builder.addCase(getGarageListAction.pending, (state) => {
      state.isLoadingGetGarageList = true;
    });
    builder.addCase(getGarageListAction.fulfilled, (state, { payload }) => {
      state.isLoadingGetGarageList = false;
      state.myGarageList = payload
        .filter((item: any) => !!item.Car)
        .map((item: any) => {
          const carInfo = CarsList.find((car) => car.id === item.Car.carType);
          return {
            id: item.id,
            userId: item.user_id,
            address: item.address,
            itemId: item.item_id,
            city: item.city,
            removed: item.removed,
            status: item.status,
            timestamp: item.timestamp,
            car: {
              carType: item.Car.carType,
              damagePercent: item.Car.damagePercent,
              timestamp: item.Car.timestamp,
              block: item.Car.block,
              value:
                ((carInfo?.basePrice || 0) * (100 - item.Car.damagePercent)) /
                100,
            },
            carType: carInfo,
          };
        }) as CarInventoryType[];
    });
    builder.addCase(getGarageListAction.rejected, (state) => {
      state.isLoadingGetGarageList = false;
    });

    builder.addCase(getUserHelperBotInfo.pending, (state) => {
      state.isLoadingHelperBotInfo = true;
    });
    builder.addCase(getUserHelperBotInfo.fulfilled, (state, { payload }) => {
      state.userCrimeBotInfo = payload.userCrimeBotInfo;
      state.userCarBotInfo = payload.userCarBotInfo;
      state.userKSBotInfo = payload.userKSBotInfo;
      state.userNarcsBotInfo = payload.userNarcsBotInfo;
      state.userBoozeBotInfo = payload.userBoozeBotInfo;
      state.userBulletBotInfo = payload.userBulletBotInfo;
      state.userBulletBotPlusInfo = payload.userBulletBotPlusInfo;

      state.isLoadingHelperBotInfo = false;
    });
    builder.addCase(getUserHelperBotInfo.rejected, (state) => {
      state.isLoadingHelperBotInfo = false;
    });

    builder.addCase(startCrimeBotAction.pending, (state) => {
      state.isStartingHelperBot = true;
    });
    builder.addCase(startCrimeBotAction.fulfilled, (state) => {
      state.isStartingHelperBot = false;
    });
    builder.addCase(startCrimeBotAction.rejected, (state) => {
      state.isStartingHelperBot = false;
    });

    builder.addCase(endCrimeBotAction.pending, (state) => {
      state.isEndingHelperBot = true;
    });
    builder.addCase(endCrimeBotAction.fulfilled, (state) => {
      state.isEndingHelperBot = false;
    });
    builder.addCase(endCrimeBotAction.rejected, (state) => {
      state.isEndingHelperBot = false;
    });

    builder.addCase(startCarBotAction.pending, (state) => {
      state.isStartingHelperBot = true;
    });
    builder.addCase(startCarBotAction.fulfilled, (state) => {
      state.isStartingHelperBot = false;
    });
    builder.addCase(startCarBotAction.rejected, (state) => {
      state.isStartingHelperBot = false;
    });

    builder.addCase(endCarBotAction.pending, (state) => {
      state.isEndingHelperBot = true;
    });
    builder.addCase(endCarBotAction.fulfilled, (state) => {
      state.isEndingHelperBot = false;
    });
    builder.addCase(endCarBotAction.rejected, (state) => {
      state.isEndingHelperBot = false;
    });

    builder.addCase(startKSBotAction.pending, (state) => {
      state.isStartingHelperBot = true;
    });
    builder.addCase(startKSBotAction.fulfilled, (state) => {
      state.isStartingHelperBot = false;
    });
    builder.addCase(startKSBotAction.rejected, (state) => {
      state.isStartingHelperBot = false;
    });
    builder.addCase(endKSBotAction.pending, (state) => {
      state.isEndingHelperBot = true;
    });
    builder.addCase(endKSBotAction.fulfilled, (state) => {
      state.isEndingHelperBot = false;
    });
    builder.addCase(endKSBotAction.rejected, (state) => {
      state.isEndingHelperBot = false;
    });

    builder.addCase(approveHelperCreditAction.pending, (state) => {
      state.isApprovingHelperCredit = true;
    });
    builder.addCase(approveHelperCreditAction.fulfilled, (state) => {
      state.isApprovingHelperCredit = false;
    });
    builder.addCase(approveHelperCreditAction.rejected, (state) => {
      state.isApprovingHelperCredit = false;
    });

    builder.addCase(startBoozeBotAction.pending, (state) => {
      state.isStartingHelperBot = true;
    });
    builder.addCase(startBoozeBotAction.fulfilled, (state) => {
      state.isStartingHelperBot = false;
    });
    builder.addCase(startBoozeBotAction.rejected, (state) => {
      state.isStartingHelperBot = false;
    });

    builder.addCase(endBoozeBotAction.pending, (state) => {
      state.isEndingHelperBot = true;
    });
    builder.addCase(endBoozeBotAction.fulfilled, (state) => {
      state.isEndingHelperBot = false;
    });
    builder.addCase(endBoozeBotAction.rejected, (state) => {
      state.isEndingHelperBot = false;
    });

    builder.addCase(startNarcsBotAction.pending, (state) => {
      state.isStartingHelperBot = true;
    });
    builder.addCase(startNarcsBotAction.fulfilled, (state) => {
      state.isStartingHelperBot = false;
    });
    builder.addCase(startNarcsBotAction.rejected, (state) => {
      state.isStartingHelperBot = false;
    });

    builder.addCase(endNarcsBotAction.pending, (state) => {
      state.isEndingHelperBot = true;
    });
    builder.addCase(endNarcsBotAction.fulfilled, (state) => {
      state.isEndingHelperBot = false;
    });
    builder.addCase(endNarcsBotAction.rejected, (state) => {
      state.isEndingHelperBot = false;
    });

    builder.addCase(claimBoozeProfitAction.pending, (state) => {
      state.isClaimingSmugglingProfit = true;
    });
    builder.addCase(claimBoozeProfitAction.fulfilled, (state) => {
      state.isClaimingSmugglingProfit = false;
    });
    builder.addCase(claimBoozeProfitAction.rejected, (state) => {
      state.isClaimingSmugglingProfit = false;
    });

    builder.addCase(claimNarcsProfitAction.pending, (state) => {
      state.isClaimingSmugglingProfit = true;
    });
    builder.addCase(claimNarcsProfitAction.fulfilled, (state) => {
      state.isClaimingSmugglingProfit = false;
    });
    builder.addCase(claimNarcsProfitAction.rejected, (state) => {
      state.isClaimingSmugglingProfit = false;
    });

    builder.addCase(getNextBustOutTimeAction.pending, () => {});
    builder.addCase(
      getNextBustOutTimeAction.fulfilled,
      (state, { payload }) => {
        state.nextBustOutTime = payload;
      }
    );
    builder.addCase(getNextBustOutTimeAction.rejected, (state) => {
      state.nextBustOutTime = -1;
    });

    builder.addCase(getBoozeTotalProfitAction.pending, (state) => {});
    builder.addCase(
      getBoozeTotalProfitAction.fulfilled,
      (state, { payload }) => {
        state.boozeTotalProfit = payload;
      }
    );
    builder.addCase(getBoozeTotalProfitAction.rejected, (state) => {});

    builder.addCase(getNarcoTotalProfitAction.pending, (state) => {});
    builder.addCase(
      getNarcoTotalProfitAction.fulfilled,
      (state, { payload }) => {
        state.narcoTotalProfit = payload;
      }
    );
    builder.addCase(getNarcoTotalProfitAction.rejected, (state) => {});

    builder.addCase(startBulletDealerBotAction.pending, (state) => {
      state.isStartingHelperBot = true;
    });
    builder.addCase(startBulletDealerBotAction.fulfilled, (state) => {
      state.isStartingHelperBot = false;
    });
    builder.addCase(startBulletDealerBotAction.rejected, (state) => {
      state.isStartingHelperBot = false;
    });

    builder.addCase(endBulletDealerBotAction.pending, (state) => {
      state.isEndingHelperBot = true;
    });
    builder.addCase(endBulletDealerBotAction.fulfilled, (state) => {
      state.isEndingHelperBot = false;
    });
    builder.addCase(endBulletDealerBotAction.rejected, (state) => {
      state.isEndingHelperBot = false;
    });

    builder.addCase(transferGameCashAction.pending, (state) => {
      state.isTransferringGameCash = true;
    });
    builder.addCase(transferGameCashAction.fulfilled, (state) => {
      state.isTransferringGameCash = false;
    });
    builder.addCase(transferGameCashAction.rejected, (state) => {
      state.isTransferringGameCash = false;
    });

    builder.addCase(getLastTransferTimeAction.pending, (state) => {});
    builder.addCase(
      getLastTransferTimeAction.fulfilled,
      (state, { payload }) => {
        state.lastTransferTime = Number(payload);
      }
    );
    builder.addCase(getLastTransferTimeAction.rejected, (state) => {});
  },
});

export const { finishCrime, finishNick, finishTrain, finishSmuggle } =
  gameSlice.actions;

export default gameSlice.reducer;
